import { useMemo } from 'react'

import { Checkbox, makeStyles, TextField, useTheme, Box, BoxProps } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import SearchIcon from '@material-ui/icons/Search'

import { FilterLabel } from './FilterLabel'
import { FilterDialog } from './FilterDialog'

import { option, OptionType } from 'modules'

import { useBoolean, useWindowView } from 'hooks'

const useStyles = makeStyles(theme => ({
    paper: {
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.23)'
    },
    listbox: {
        padding: 0
    },
    option: {
        padding: theme.spacing(0.5, 1),
        fontSize: theme.typography.pxToRem(14),
        lineHeight: theme.typography.pxToRem(20)
    },
    checkbox: {
        padding: 4
    }
}))

type ValuesType = Array<string>

type Props = {
    value: ValuesType
    onChange: (newValues: ValuesType, oldValues: ValuesType) => void
    options: Array<OptionType>
    defaultLabel: string
    defaultOption: OptionType
    labelIcon?: null | JSX.Element
    multipleItemsLabel?: string
    width: BoxProps['width']
    minWidth?: number
}

export const MultipleSelect = ({
    value,
    onChange,
    options,
    defaultLabel,
    labelIcon,
    defaultOption,
    width,
    minWidth,
    multipleItemsLabel
}: Props) => {
    const theme = useTheme()
    const classes = useStyles()

    const { isMobileView, isDesktopView } = useWindowView()

    const allOptions = defaultOption ? [defaultOption, ...options] : options

    const selectedOptions = allOptions.filter(o => value.includes(o.value))
    const selectedCount = selectedOptions.length

    const open = useBoolean()

    const color =
        selectedCount === 0 ||
        (defaultOption && selectedOptions.map(o => o.value).includes(defaultOption.value))
            ? theme.palette.text.primary
            : theme.palette.primary.main

    const labelProps = {
        defaultLabel,
        selectedCount,
        label: selectedOptions?.[0]?.label || '',
        multipleItemsLabel,
        color,
        width,
        transparent: isDesktopView
    }

    if (isMobileView) {
        return (
            <Box>
                <FilterLabel {...labelProps} onClick={open.setTrue} />
                <FilterDialog
                    isOpen={open.isTrue}
                    onConfirm={newValues => {
                        onChange(
                            newValues,
                            selectedOptions.map(o => o.value)
                        )

                        selectedOptions.map(o => o.value)
                    }}
                    onClose={open.setFalse}
                    options={allOptions}
                />
            </Box>
        )
    }

    if (open.isFalse) {
        return <FilterLabel onFocus={open.setTrue} labelIcon={labelIcon} {...labelProps} />
    }

    return (
        <Autocomplete
            open
            innerRef={node => {
                if (node) {
                    const input = node.getElementsByTagName('input')[0]
                    input?.select()
                }
            }}
            autoHighlight
            multiple
            disableCloseOnSelect
            disableListWrap
            fullWidth
            style={{ minWidth, maxWidth: labelProps.width }}
            popupIcon={null}
            value={selectedOptions}
            data-cy="filter-autocomplete"
            onChange={(_event, newValue) => {
                onChange(
                    newValue.map(o => o.value),
                    selectedOptions.map(o => o.value)
                )
            }}
            onBlur={open.setFalse}
            options={allOptions}
            renderInput={({ InputProps, ...params }) => (
                <TextField
                    {...params}
                    data-cy="filter-text-field"
                    InputProps={{
                        ...InputProps,
                        fullWidth: true,
                        disableUnderline: true,
                        startAdornment: <SearchIcon />
                    }}
                    onKeyDown={event => {
                        // Disable selected items deletion by pressing backspace
                        if (event.keyCode === 8) {
                            event.stopPropagation()
                        }
                    }}
                />
            )}
            renderOption={(o, { selected }) => (
                <>
                    <Checkbox
                        checked={selected}
                        className={classes.checkbox}
                        color="primary"
                        data-cy="filter-checkbox"
                    />
                    {o.label}
                </>
            )}
            getOptionLabel={o => o.label || ''} // Temporary hack
            classes={{
                paper: classes.paper,
                listbox: classes.listbox,
                option: classes.option
            }}
        />
    )
}

export const allIfEmpty = (values: ValuesType) => (values.length === 0 ? ['ALL'] : values)

export const handleChange =
    (allValues: ValuesType) => (values: ValuesType, prevValues: ValuesType) => {
        if (values.length === 0 && prevValues[0] === 'ALL') {
            return allValues
        }

        if (values.length === 0 || values[values.length - 1] === 'ALL') {
            return []
        }

        return values.filter(value => value !== 'ALL')
    }

type MultipleProps = {
    value: ValuesType
    onChange: (values: ValuesType) => void
    options: Array<OptionType>
    labelIcon?: null | JSX.Element
    multipleItemsLabel?: string
    width?: BoxProps['width']
    minWidth?: number
}

export const FilterSelectMultiple = (props: MultipleProps) => {
    const getNewValue = useMemo(
        () => handleChange(props.options.map(option => option.value)),
        [props.options]
    )

    return (
        <MultipleSelect
            value={allIfEmpty(props.value)}
            width={props.width || 'auto'}
            minWidth={props.minWidth || 0}
            onChange={(values, prevValues) => {
                props.onChange(getNewValue(values, prevValues))
            }}
            multipleItemsLabel={props.multipleItemsLabel}
            defaultLabel={`All ${props.multipleItemsLabel}`}
            labelIcon={props.labelIcon}
            options={props.options}
            defaultOption={option('ALL', `All ${props.multipleItemsLabel}`)}
        />
    )
}
